import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * AuthCallback — mounted when the URL fragment contains `session_id=…`.
 * Exchanges the session_id for a persistent session token, then hands the
 * user off to the community hub (or wherever they were headed).
 */
export default function AuthCallback() {
  const { exchangeOAuthSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  // Synchronous guard: run only once even under React StrictMode double-render.
  if (!processed.current) {
    processed.current = true;
  }

  useEffect(() => {
    (async () => {
      const hash = window.location.hash || "";
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : null;
      if (!sessionId) {
        navigate("/", { replace: true });
        return;
      }
      // Strip the fragment so a refresh doesn't retry the exchange.
      const cleanPath = location.pathname + location.search;
      window.history.replaceState({}, document.title, cleanPath || "/");
      const result = await exchangeOAuthSession(sessionId);
      navigate(result.ok ? "/community" : "/", { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="auth-callback"
      className="min-h-[60vh] flex flex-col items-center justify-center text-slate-300 gap-3"
    >
      <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
      <p className="text-sm">Finishing sign-in…</p>
    </div>
  );
}
